<template>
  <Popover
    v-bind="{
      statelessBs: {
        maxWidth: 272,
        minWidth: 240,
        maxHeight: 280,
        overflow: 'auto',
      },
      ...popoverProps,
    }"
    :on-open="() => toggleOpen(true)"
    :on-close="() => toggleOpen(false)"
    :is-shown="isShowSelect"
    trigger="manual"
  >
    <!--
      @slot 选项控制区域
      @binding {func} itemToText 用来显示当前选项文本
      @binding {object} itemNameByIdentity 根据 id 来显示选项文本，在选项值是非 object 使用
      @binding {func} toggleOpen 折叠选项框
      @binding {bool} isOpen 选项框是否当前展示
    -->
    <slot
      :itemToText="options.itemToText"
      :itemNameByIdentity="itemNameByIdentity"
      :toggleOpen="toggleOpen"
      :isOpen="isShowSelect"
      name="select"
    />

    <template #popover>
      <!-- // option - searchbar -->
      <NewBsBox
        v-if="filterable"
        :bs="searchBarBs"
      >
        <SearchInput
          ref="searchInputRef"
          :model-value="filterText"
          :placeholder="searchMissingAdd ? `${searchPlaceholder}或添加` : searchPlaceholder"
          :bs="{ width: '100%', mx: 12, mt: 4 }"
          @on-change="onChangeFilter"
          @on-enter="onAddSearch"
        />
      </NewBsBox>

      <!-- // option - content -->
      <NewBsBox :bs="optionBs">
        <EmptyText
          v-if="loadError"
          :bs="{ p: 16, }"
        >
          <div>加载超时，请重试</div>
          <BeerIcon
            :size="14"
            :bs="{ ml: 8, cursor: 'pointer' }"
            :grid-size="36"
            :icon-path="$options.RETRY_PATH"
            color="danger"
            @click.stop="handleRetry"
          />
        </EmptyText>
        <EmptyText
          v-else-if="loading"
        >
          <LoadingSpinner
            :size="18"
          />
          <NewBsBox :bs="{ ml: 8, color: 'brand' }">
            加载中...
          </NewBsBox>
        </EmptyText>
        <template v-else>
          <!--
            @slot 选项列表 slot
            @binding {func} isEqual 是否选中判断函数
            @binding {array} extItems 经过转换的 items，带 computed 内部属性
          -->
          <slot
            :isEqual="isEqual"
            :extItems="extItems"
            name="options"
          />
          <slot name="emptyOptions">
            <EmptyText
              v-if="filterable && extItems.length < 1"
            >
              <div style="text-align: center;">
                {{ noMatchText }}
                <BeerButton
                  v-if="filterText && searchMissingAdd"
                  :bs="{ display: 'block', mr: 0, mt: 8 }"
                  size="s"
                  variant="text"
                  @click.stop="onAddSearch"
                >
                  {{ addActionText }}「{{ filterText }}」
                </BeerButton>
              </div>
            </EmptyText>
          </slot>
        </template>
      </NewBsBox>

      <!-- option - actionbar -->
      <NewBsBox
        v-if="!loading && showAction"
        :bs="actionBarBs"
      >
        <!--
          @slot 选项框底部显示的 slot，一般显示action
          @binding {func} toggleOpen 折叠选项框
        -->
        <slot
          :toggleOpen="toggleOpen"
          name="action"
        />
      </NewBsBox>
    </template>
  </Popover>
</template>

<script lang="ts">
  import {
    defineComponent, ref, computed, nextTick,
    PropType,
  } from 'vue'
  import NewBsBox from '@xhs/yam-beer/cmp/primitive/NewBsBox'
  import Popover from '@xhs/yam-beer/cmp/overlayers/Popover/Popover.vue'
  import SearchInput from '@xhs/yam-beer/cmp/dataEntry/Search/SearchInput.vue'
  import WithBsBox from '@xhs/yam-beer/cmp/primitive/WithBsBox'
  import LoadingSpinner from '@xhs/yam-beer/cmp/common/Spinner/LoadingSpinner.vue'
  import BeerButton from '@xhs/yam-beer/cmp/common/Button/Button.vue'
  import BeerIcon from '@xhs/yam-beer/cmp/common/Icon/Icon.vue'
  import { sortBy } from 'lodash'
  import {
    RETRY_PATH,
  } from '@xhs/yam-beer/cmp/constants/builtinIcon'
  import useDownshift, {
    Options,
    ExtendItem,
  } from '../../primitive/UseDownshift'
  import { BeerStyleObj } from '../../../core'

  const ACTIONLBAR_HEIGHT = 46
  const OPTION_HEIGH = 272
  const SERARCH_HEIGHT = 36

  const EmptyText = WithBsBox({
    bs: {
      color: 'mute',
      minHeight: 160,
      fontSize: '14px',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  })

  /**
   * 一种 select 布局，并内置 downshift 用法
   * 目前在 Single / Multi / PagerOption select 复用
   */
  export default defineComponent({
    name: 'SelectBase',
    components: {
      NewBsBox,
      Popover,
      SearchInput,
      EmptyText,
      LoadingSpinner,
      BeerButton,
      BeerIcon,
    },
    RETRY_PATH,
    inheritAttrs: false,
    props: {
      loading: {
        type: Boolean,
        default: false,
      },
      loadError: {
        type: String,
        default: undefined,
      },
      /**
       * 是否禁止选
       */
      disabled: {
        type: Boolean,
        default: false,
      },
      wrapperBs: {
        type: Object,
        default: () => ({}),
      },
      /**
       * 透传给 downshift 的参数
       */
      downshiftProps: {
        type: Object as PropType<Options>,
        default: () => ({}),
      },
      /**
       * 透传给 popover 参数
       */
      popoverProps: {
        type: Object,
        default: () => ({}),
      },
      /**
       * 是否可以过滤
       */
      filterable: {
        type: Boolean,
        default: false,
      },
      sortOptionOnInput: {
        type: Boolean,
        default: false,
      },
      /**
       * 选项过滤函数
       */
      textFilterFn: {
        type: Function,
        required: false,
      },
      /**
       * 当过滤选项不匹配时显示的文案
       */
      noMatchText: {
        type: String,
        default: '无匹配结果',
      },
      /**
       * 当过滤选项不匹配时, 是否允许添加
       */
      searchMissingAdd: {
        type: Boolean,
        default: false,
      },
      /**
       * 搜索miss 时的操作名称
       */
      addActionText: {
        type: String,
        default: '添加',
      },
      /**
       * 搜索 placeholder
       */
      searchPlaceholder: {
        type: String,
        default: '输入关键词进行搜索',
      },
      /**
       * 是否显示 action slot，如确认、清除
       */
      showAction: {
        type: Boolean,
        default: true,
      },
      /**
       * 是否默认限制选项高度
       */
      defaultMaxHeight: {
        type: Boolean,
        default: false,
      },
      /**
       * 是否跳过自动调整选项高度
       */
      skipOptionBs: {
        type: Boolean,
        default: false,
      },
      actionBs: {
        type: Object,
        default: () => ({}),
      },
      /**
       * toggle 选择的 hook
       */
      onToggle: {
        type: Function,
      },
    },
    emits: ['on-search', 'on-search-add'],
    setup(props, { emit }) {
      const searchInputRef = ref<HTMLInputElement|null>(null)
      const showPopover = ref(false)
      const filterText = ref('')
      const isShowSelect = computed(() => {
        if (props.disabled) {
          return false
        }
        return showPopover.value
      })

      const downshiftOption = computed(() => {
        const options: Options = {
          ...props.downshiftProps,
        }
        if (props.sortOptionOnInput && filterText.value) {
          options.sortFn = (opts: ExtendItem[]) => sortBy(
            opts,
            item => item.computed.text.indexOf(filterText.value),
          )
        }
        if (props.textFilterFn) {
          options.filterFn = item => props.textFilterFn?.(item, filterText.value)
        }
        return options
      })

      const {
        extItems,
        itemNameByIdentity,
        isEqual,
        options,
      } = useDownshift(downshiftOption, showPopover)

      const actionBarBs = computed<BeerStyleObj>(() => ({
        display: 'flex',
        height: ACTIONLBAR_HEIGHT,
        mb: -8,
        alignItems: 'center',
        justifyContent: 'flex-end',
        ...props.actionBs,
      }))

      const optionBs = computed(() => {
        if (props.skipOptionBs) {
          return {}
        }
        const bs: BeerStyleObj = {
          overflowY: 'auto',
        }
        let maxHeight = OPTION_HEIGH
        if (props.defaultMaxHeight) {
          bs.maxHeight = maxHeight
        }

        if (props.showAction) {
          maxHeight -= ACTIONLBAR_HEIGHT
          bs.maxHeight = maxHeight
        }
        if (props.filterable) {
          maxHeight -= SERARCH_HEIGHT
          bs.maxHeight = maxHeight
        }
        return bs
      })

      return {
        toggleOpen: (flag: boolean) => {
          let isOpen
          if (typeof flag !== 'boolean') {
            isOpen = !showPopover.value
          } else {
            isOpen = flag
          }
          if (isOpen) {
            nextTick(() => {
              searchInputRef.value?.focus()
            })
          }
          showPopover.value = isOpen
          if (props.onToggle) {
            props.onToggle(isOpen)
          }
        },
        isShowSelect,
        extItems,
        itemNameByIdentity,
        isEqual,
        options,

        filterText,
        onChangeFilter: (v: string) => {
          filterText.value = v
          emit('on-search', v)
        },
        onAddSearch: () => {
          if (!props.searchMissingAdd) {
            return
          }
          /**
           * 添加未匹配的 Filter 文字作为值
           *
           * @property {string} value - 筛选的文字
           */
          emit('on-search-add', filterText.value)
          filterText.value = ''
        },

        searchBarBs: {
          display: 'flex',
          height: SERARCH_HEIGHT,
          alignItems: 'center',
          justifyContent: 'center',
          mb: 8,
        },
        actionBarBs,
        optionBs,
      }
    },
  })
</script>
